import { Check as CheckIcon, LocalOffer as TagIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { TagChip } from "../../components/tag-chip";
import { SUGGESTED_TAGS } from "../../constants/tags";
import { useAvailableTags } from "../../hooks/use-tags";
import type { TestCase } from "./types";

interface BatchTagsDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (tags: string[]) => void;
  selectedTests: TestCase[];
  isLoading: boolean;
}

export const BatchTagsDialog: React.FC<BatchTagsDialogProps> = ({
  open,
  onClose,
  onConfirm,
  selectedTests,
  isLoading,
}) => {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const { data: availableTagsData } = useAvailableTags();
  const availableTags = availableTagsData?.tags || [];

  useEffect(() => {
    if (open) {
      setSelectedTags([]);
    }
  }, [open]);

  const handleConfirm = () => {
    if (selectedTags.length > 0) {
      onConfirm(selectedTags);
    }
  };

  const handleClose = () => {
    setSelectedTags([]);
    onClose();
  };

  // Combine available tags with suggested tags, removing duplicates
  const allSuggestedTags = [...new Set([...availableTags, ...SUGGESTED_TAGS])];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
          border: "1px solid rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
        },
      }}
      TransitionProps={{
        timeout: 200,
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TagIcon color="primary" sx={{ fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600 }}>
            Add Tags
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 0.875, pb: 0.875 }}>
        <Stack spacing={2.5}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: 13,
                fontWeight: 600,
                mb: 2,
                color: "text.primary",
              }}
            >
              Selected Tests ({selectedTests.length})
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 12, color: "text.secondary" }}>
              {selectedTests.slice(0, 5).map((test) => (
                <React.Fragment key={test.id}>
                  {test.name}
                  <br />
                </React.Fragment>
              ))}
              {selectedTests.length > 5 && (
                <Typography variant="caption" sx={{ fontSize: 11, fontStyle: "italic" }}>
                  ...and {selectedTests.length - 5} more
                </Typography>
              )}
            </Typography>
          </Paper>

          <Autocomplete
            multiple
            size="small"
            options={allSuggestedTags}
            value={selectedTags}
            onChange={(_, value) => setSelectedTags(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tags"
                placeholder="Type to search or create tags..."
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.875rem",
                  },
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <TagChip
                  {...getTagProps({ index })}
                  key={option}
                  tag={option}
                  size="small"
                  sx={{
                    height: 22,
                    "& .MuiChip-deleteIcon": {
                      fontSize: "0.875rem",
                    },
                  }}
                />
              ))
            }
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <li
                  key={key}
                  {...otherProps}
                  style={{
                    fontSize: "0.875rem",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <TagChip
                    tag={option}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 20,
                      mr: 1,
                    }}
                  />
                </li>
              );
            }}
            freeSolo
            autoComplete
            includeInputInList
            filterSelectedOptions
            clearOnBlur
            handleHomeEndKeys
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 0.875, justifyContent: "flex-end" }}>
        <IconButton
          onClick={handleConfirm}
          disabled={selectedTags.length === 0 || isLoading}
          sx={{
            width: 40,
            height: 40,
            color: "grey.600",
            "&:hover": {
              bgcolor: "grey.300",
            },
            "&:disabled": {
              bgcolor: "grey.200",
              color: "grey.400",
            },
          }}
        >
          <CheckIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogActions>
    </Dialog>
  );
};
